import { HTMLAttributes, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@components/common/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form";
import { Input } from "@components/common/input";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { DictType } from "@pma/types";
import {
  RESET_PASSWORD_LINK_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { cn } from "@pma/utils/helpers";
import { toast } from "@hooks/use-toast";
import { Card } from "@components/common/card";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LOGIN_PAGE_ROUTE } from "@pma/utils/page-routes";
import Recaptcha from "./recaptcha";
import useTranslator from "@hooks/use-translator";

interface ResetPasswordLinkProps extends HTMLAttributes<HTMLDivElement> {}

const formSchema = z.object({
  email: z
    .string()
    .min(1, { message: "Please enter your email" })
    .email({ message: "Invalid email address" }),
});

function ResetPasswordLinkForm({
  className,
  ...props
}: ResetPasswordLinkProps) {
  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  });
  const [submittedData, setSubmittedData] = useState({});
  const [v2Fallback, setV2Fallback] = useState(false);

  const { translate } = useTranslator();

  const currentPath = useLocation().pathname;
  const navigate = useNavigate()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  });

  const sendResetLink = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Failed to complete reCAPTCHA"),
        variant: "info",
      });
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return;
    }

    const isLocalHost = process.env.NODE_ENV === "development";
    const isDev = currentPath.includes("https://rog-tech.io/dev.pms");
    const originEnvironment = isDev
      ? {
          for_test: false,
        }
      : isLocalHost
        ? {
            for_test: true,
          }
        : {
            for_test: false,
          };

    const response = await axios.post(
      SERVER_URL + RESET_PASSWORD_LINK_ENDPOINT,
      {
        user_email: payload.email,
        ...originEnvironment,
        recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
        recaptcha_response_v2: payload?.v2Token,
      },
      {},
    );

    return response.data;
  };

  const sendResetLinkMutation = useMutation({
    mutationKey: ["reset-link"],
    mutationFn: (payload: DictType) => sendResetLink(payload),
    onSuccess: (data: DictType) => {
      toast({
        title: translate("Success"),
        description: translate("Reset link sent to your email"),
        variant: "success",
      });

      navigate(LOGIN_PAGE_ROUTE);

    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      if (errorMessage.toLocaleLoweCase() === "invalid captcha response") {
        setV2Fallback(true);
      }
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to send reset link"),
        variant: "error",
      });
    },
  });

  function onSubmit(data: z.infer<typeof formSchema>) {
    // Track for v2 resubmission
    setSubmittedData(data);

    sendResetLinkMutation.mutate({ email: data.email });
  }

  const reSubmit = (token: string) => {
    sendResetLinkMutation.mutate({ ...submittedData, v2Token: token });
  };

  return (
    <Card
      className="h-[450px] w-full rounded-md border-b-0 border-none p-6 md:w-[350px]"
      style={{
        background: "linear-gradient(blue 5.01%, rgb(232, 203, 203, 0%)",
        borderRadius: "15px 15px 0px 0px",
      }}
    >
      <div className="flex flex-col items-center justify-center space-y-2 text-left">
        <div className="relative mt-0 p-0">
          <img
            src={`${process.env.PUBLIC_URL}/group-232.svg`}
            alt="Brand logo"
          />
        </div>

        <div className="my-2 flex flex-col space-y-2 text-left">
          <h1 className="text-md font-semibold capitalize tracking-tight text-white">
            {translate("Request reset password")}{" "}
          </h1>
        </div>
      </div>
      <div className={cn("grid gap-6", className)} {...props}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-2 text-left">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-1 text-white">
                    <FormLabel>{translate("Email")}</FormLabel>
                    <FormControl>
                      <Input
                        className="rounded-full text-black"
                        placeholder="rog@mail.com"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                className="mt-2 rounded-full"
                loading={sendResetLinkMutation.isPending}
                disabled={sendResetLinkMutation.isPending}
              >
                {translate("Send Request")}{" "}
              </Button>
            </div>
          </form>
        </Form>

        <div className="relative my-2">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-foreground">
              {translate("or")}{" "}
            </span>
          </div>
        </div>
        <Button className="rounded-full bg-accent text-primary hover:bg-secondary/40 hover:text-white">
          <Link to={LOGIN_PAGE_ROUTE}>{translate("Back to Login")}</Link>
        </Button>
      </div>

      {/* GOOGLE Recaptcha */}
      <Recaptcha
        setToken={setRecaptchaToken}
        v2={v2Fallback}
        reSubmit={reSubmit}
      />
    </Card>
  );
}

export default ResetPasswordLinkForm;
